import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    getSystemConfig, 
    updateSystemConfig, 
    getSystemStatus, 
    getClientMaintenance, 
    updateClientMaintenance as apiUpdateClientMaintenance, 
    disableClientMaintenance as apiDisableClientMaintenance
} from '../api/systemApi';
import { fetchOrganizations } from '../api/auth0Api';
import { useAuth0 } from '../components/auth/DevAuth0Provider';

const SettingsContext = createContext();

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [settings, setSettings] = useState({
        maintenance_mode: false,
        debug_logging: false,
        api_logging: false
    });
    const [clientMaintenance, setClientMaintenance] = useState([]);
    const [availableClients, setAvailableClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch initial settings
    const fetchSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch system config and maintenance status
            const [configData, maintenanceData] = await Promise.all([
                getSystemConfig(),
                getClientMaintenance()
            ]);
            
            setSettings(configData);
            setClientMaintenance(maintenanceData.clients || []);
            
            // Fetch available clients using the same method as Clients.jsx
            const organizations = await fetchOrganizations(getAccessTokenSilently);
            const clients = organizations.map(org => ({
                id: org.metadata?.customer_id || org.name.toLowerCase(),
                name: org.display_name || org.name,
                displayName: org.display_name || org.name,
                category: org.metadata?.category || 'general',
                organizationId: org.id
            }));
            
            // Remove duplicate clients (e.g., Helios/Celeste duplicates)
            const uniqueClients = clients.filter((client, index, self) => {
                // Check if this is a duplicate by exact name match
                const isExactDuplicate = index !== self.findIndex(c => 
                    c.name.toLowerCase() === client.name.toLowerCase()
                );
                
                // If it's an exact duplicate, remove it
                if (isExactDuplicate) {
                    return false;
                }
                
                // Special case: if we have both Helios and Celeste, keep only Helios
                if (client.name.toLowerCase().includes('celeste')) {
                    const hasHelios = self.some(c => c.name.toLowerCase().includes('helios'));
                    return !hasHelios; // Only keep Celeste if there's no Helios
                }
                
                // Keep all other clients
                return true;
            });
            
            setAvailableClients(uniqueClients);
            
        } catch (err) {
            console.error('Failed to fetch system settings:', err);
            setError('Failed to load system settings');
        } finally {
            setLoading(false);
        }
    };

    // Update a specific setting
    const updateSetting = async (key, value) => {
        try {
            setError(null);
            console.log(`🔄 Updating ${key} to ${value}...`);
            
            const response = await updateSystemConfig({
                [key]: value
            });
            
            console.log(`✅ Backend response for ${key}:`, response);
            
            // Update local state with confirmed backend value
            setSettings(prev => ({
                ...prev,
                [key]: value
            }));
            
            // Show success message for logging settings
            if (key === 'debug_logging' || key === 'api_logging') {
                console.log(`✅ ${key.replace('_', ' ').toUpperCase()} ${value ? 'enabled' : 'disabled'} successfully`);
            }
            
            return response;
        } catch (err) {
            console.error(`❌ Failed to update ${key}:`, err);
            const errorMessage = err.response?.data?.message || `Failed to update ${key.replace('_', ' ')}`;
            setError(errorMessage);
            
            // Revert UI state on error
            await fetchSettings();
            
            throw err;
        }
    };

    // Update client maintenance mode
    const updateClientMaintenance = async (clientIds) => {
        try {
            setError(null);
            const response = await apiUpdateClientMaintenance(clientIds);
            setClientMaintenance(clientIds);
            return response;
        } catch (err) {
            console.error('Failed to update client maintenance:', err);
            setError('Failed to update client maintenance');
            throw err;
        }
    };

    // Disable client maintenance mode
    const disableClientMaintenance = async () => {
        try {
            setError(null);
            await apiDisableClientMaintenance();
            setClientMaintenance([]);
        } catch (err) {
            console.error('Failed to disable client maintenance:', err);
            setError('Failed to disable client maintenance');
            throw err;
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const value = {
        settings,
        clientMaintenance,
        availableClients,
        loading,
        error,
        updateSetting,
        updateClientMaintenance,
        disableClientMaintenance,
        refreshSettings: fetchSettings
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};